import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';
import { useToast } from '../../hooks/use-toast';
import DocumentPreviewModal from './DocumentPreviewModal';
import { 
  Upload,
  Search,
  Filter,
  Download,
  Eye,
  Edit3,
  Trash2,
  Star,
  StarOff,
  FileText,
  Calendar,
  Tag,
  MoreVertical,
  Plus,
  Folder,
  SortAsc,
  SortDesc
} from 'lucide-react';
import axios from 'axios';

const DocumentVault = () => {
  const [documents, setDocuments] = useState([]);
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('date_desc');
  const [viewMode, setViewMode] = useState('card'); // 'card' or 'list'
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();

  const API_BASE = process.env.REACT_APP_BACKEND_URL; // Already includes /api

  // Mock documents for demonstration - in real implementation, these would come from API
  const mockDocuments = [
    {
      id: '1',
      title: 'Testimony Under God - Traffic',
      category: 'traffic',
      content: `TESTIMONY UNDER GOD - TRAFFIC VIOLATION CASE

TO: [Officer/Court Name]
FROM: [Your Name], Private National
DATE: [Current Date]

CONDITIONAL ACCEPTANCE

I, [Your Name], a living man/woman and Private National, conditionally accept your traffic citation for value upon proof of jurisdiction and standing.

CONDITIONS FOR ACCEPTANCE:
1. Provide verified proof of personal injury or property damage
2. Provide constitutional authority for the traffic stop
3. Provide sworn affidavit of personal knowledge of violation

NOTICE TO AGENT IS NOTICE TO PRINCIPAL
NOTICE TO PRINCIPAL IS NOTICE TO AGENT

Without full disclosure and acceptance of these conditions, this matter remains in administrative default.

Sincerely,
[Your Signature]
[Your Name], Private National`,
      created_at: '2025-08-21T00:00:00Z',
      updated_at: '2025-08-21T00:00:00Z',
      favorite: true,
      tags: ['testimony', 'traffic', 'conditional-acceptance'],
      file_size: '2.1 KB'
    },
    {
      id: '2',
      title: 'Writ of Mandamus - Property',
      category: 'property',
      content: `WRIT OF MANDAMUS - PROPERTY RIGHTS

TO: [Government Agency]
FROM: [Your Name], Private National
RE: Unlawful Property Actions

DEMAND FOR MANDAMUS

I, [Your Name], hereby demand immediate cessation of unlawful actions against my private property located at [Property Address].

CONSTITUTIONAL VIOLATIONS:
- 4th Amendment: Unreasonable search/seizure
- 5th Amendment: Due process violations
- 14th Amendment: Equal protection denial

REMEDY DEMANDED:
1. Immediate return of property
2. Compensation for damages
3. Written guarantee of future compliance

Time is of the essence. Failure to respond within 10 days constitutes agreement to these terms.

[Your Signature]
[Your Name], Private National`,
      created_at: '2025-08-10T00:00:00Z',
      updated_at: '2025-08-10T00:00:00Z',
      favorite: false,
      tags: ['mandamus', 'property', 'constitutional'],
      file_size: '1.8 KB'
    },
    {
      id: '3',
      title: 'Conditional Acceptance - Contract',
      category: 'contract',
      content: `CONDITIONAL ACCEPTANCE FOR VALUE

TO: [Contract Party]
FROM: [Your Name], Private National
RE: Contract Dispute

CONDITIONAL ACCEPTANCE

I conditionally accept your contract terms for value, upon proof of the following:

CONDITIONS PRECEDENT:
1. Full disclosure of all material facts
2. Proof of consideration received
3. Evidence of meeting of the minds
4. Constitutional authority for enforcement

MAXIMS OF EQUITY APPLICABLE:
- Equity will not perfect an imperfect gift
- He who seeks equity must do equity
- Equity follows the law

Until these conditions are satisfied, no binding contract exists.

[Your Signature]
[Your Name], Private National`,
      created_at: '2025-07-30T00:00:00Z',
      updated_at: '2025-07-30T00:00:00Z',
      favorite: true,
      tags: ['contract', 'conditional-acceptance', 'equity'],
      file_size: '1.5 KB'
    }
  ];

  const categories = [
    { value: 'all', label: 'All Categories', color: 'blue' },
    { value: 'traffic', label: 'Traffic', color: 'red' },
    { value: 'property', label: 'Property', color: 'green' },
    { value: 'contract', label: 'Contract', color: 'blue' },
    { value: 'criminal-defense', label: 'Criminal Defense', color: 'gray' },
    { value: 'family', label: 'Family', color: 'purple' },
    { value: 'taxation', label: 'Taxation', color: 'yellow' },
    { value: 'estate-trusts', label: 'Estate/Trusts', color: 'indigo' }
  ];

  const sortOptions = [
    { value: 'date_desc', label: 'Newest First' },
    { value: 'date_asc', label: 'Oldest First' },
    { value: 'name_asc', label: 'Name A-Z' },
    { value: 'name_desc', label: 'Name Z-A' },
    { value: 'category', label: 'Category' }
  ];

  useEffect(() => {
    loadDocuments();
    
    // Listen for custom event when documents are saved
    const handleDocumentAdded = () => {
      loadDocuments();
    };
    
    window.addEventListener('documentSaved', handleDocumentAdded);
    
    return () => {
      window.removeEventListener('documentSaved', handleDocumentAdded);
    };
  }, []);

  const loadDocuments = () => {
    // Load documents from localStorage (saved documents) and combine with mock documents
    const savedDocs = JSON.parse(localStorage.getItem('document_vault') || '[]');
    const allDocuments = [...savedDocs, ...mockDocuments];
    setDocuments(allDocuments);
    setLoading(false);
  };

  useEffect(() => {
    filterAndSortDocuments();
  }, [documents, searchTerm, selectedCategory, sortBy]);

  const filterAndSortDocuments = () => {
    let filtered = [...documents];

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(doc => 
        doc.title.toLowerCase().includes(term) ||
        doc.content.toLowerCase().includes(term) ||
        doc.tags.some(tag => tag.toLowerCase().includes(term))
      );
    }

    // Apply category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(doc => doc.category === selectedCategory);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'date_desc':
          return new Date(b.created_at) - new Date(a.created_at);
        case 'date_asc':
          return new Date(a.created_at) - new Date(b.created_at);
        case 'name_asc':
          return a.title.localeCompare(b.title);
        case 'name_desc':
          return b.title.localeCompare(a.title);
        case 'category':
          return a.category.localeCompare(b.category);
        default:
          return 0;
      }
    });

    setFilteredDocuments(filtered);
  };

  const getCategoryColor = (category) => {
    const categoryData = categories.find(cat => cat.value === category);
    return categoryData?.color || 'gray';
  };

  const getCategoryBadgeClass = (color) => {
    const colorMap = {
      red: 'bg-red-100 text-red-700 border-red-200',
      green: 'bg-green-100 text-green-700 border-green-200',
      blue: 'bg-blue-100 text-blue-700 border-blue-200',
      gray: 'bg-gray-100 text-gray-700 border-gray-200',
      purple: 'bg-purple-100 text-purple-700 border-purple-200',
      yellow: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      indigo: 'bg-indigo-100 text-indigo-700 border-indigo-200'
    };
    return colorMap[color] || colorMap.gray;
  };

  const toggleFavorite = (documentId) => {
    // Update state
    setDocuments(prev => prev.map(doc => 
      doc.id === documentId 
        ? { ...doc, favorite: !doc.favorite }
        : doc
    ));
    
    // Update localStorage if it's a saved document
    const savedDocs = JSON.parse(localStorage.getItem('document_vault') || '[]');
    const updatedDocs = savedDocs.map(doc => 
      doc.id === documentId 
        ? { ...doc, favorite: !doc.favorite }
        : doc
    );
    localStorage.setItem('document_vault', JSON.stringify(updatedDocs));
    
    toast({
      title: "Document Updated",
      description: "Favorite status changed",
    });
  };

  const deleteDocument = (documentId) => {
    // Remove from state
    setDocuments(prev => prev.filter(doc => doc.id !== documentId));
    
    // Remove from localStorage if it's a saved document
    const savedDocs = JSON.parse(localStorage.getItem('document_vault') || '[]');
    const updatedDocs = savedDocs.filter(doc => doc.id !== documentId);
    localStorage.setItem('document_vault', JSON.stringify(updatedDocs));
    
    toast({
      title: "Document Deleted",
      description: "The document has been permanently removed",
    });
  };

  const downloadDocument = (document) => {
    const blob = new Blob([document.content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${document.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast({
      title: "Download Started",
      description: "Your document is downloading",
    });
  };

  const viewDocument = (document) => {
    setSelectedDocument(document);
    setPreviewModalOpen(true);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
          <p className="text-gray-600">Loading documents...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-gray-50 overflow-hidden">
      {/* Header */}
      <div className="bg-blue-600 text-white p-6 flex-shrink-0">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold">My Document Vault</h1>
              <p className="text-blue-100 mt-1">
                {filteredDocuments.length} of {documents.length} documents
              </p>
            </div>
            <Button 
              className="bg-white text-blue-600 hover:bg-blue-50"
              onClick={() => {
                // TODO: Implement file upload
                toast({
                  title: "Upload Feature",
                  description: "File upload functionality coming soon",
                });
              }}
            >
              <Upload className="w-4 h-4 mr-2" />
              Upload
            </Button>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white border-b p-4 flex-shrink-0">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
            {/* Search */}
            <div className="flex-1 max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Search documents, categories, or tags..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Category Filter */}
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="rounded-md border border-gray-300 px-3 py-2 text-sm"
              >
                {categories.map(category => (
                  <option key={category.value} value={category.value}>
                    {category.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Sort */}
            <div className="flex items-center gap-2">
              <SortAsc className="w-4 h-4 text-gray-400" />
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="rounded-md border border-gray-300 px-3 py-2 text-sm"
              >
                {sortOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Documents Grid - Scrollable */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-7xl mx-auto">{filteredDocuments.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {searchTerm || selectedCategory !== 'all' ? 'No documents found' : 'No documents yet'}
              </h3>
              <p className="text-gray-600 mb-6">
                {searchTerm || selectedCategory !== 'all' 
                  ? 'Try adjusting your search or filter criteria'
                  : 'Generate your first legal document using the Workflow Builder'
                }
              </p>
              {(!searchTerm && selectedCategory === 'all') && (
                <Button onClick={() => window.location.href = '/dashboard'}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create New Document
                </Button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredDocuments.map((document) => (
                <Card key={document.id} className="bg-gray-50 border-gray-200 hover:shadow-lg transition-shadow">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <CardTitle className="text-lg font-semibold text-gray-900 truncate">
                          {document.title}
                        </CardTitle>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge 
                            variant="outline" 
                            className={getCategoryBadgeClass(getCategoryColor(document.category))}
                          >
                            {categories.find(cat => cat.value === document.category)?.label || document.category}
                          </Badge>
                          {document.favorite && (
                            <Star className="w-4 h-4 text-yellow-500 fill-current" />
                          )}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-gray-400 hover:text-yellow-500 p-1"
                        onClick={() => toggleFavorite(document.id)}
                      >
                        {document.favorite ? (
                          <Star className="w-4 h-4 fill-current text-yellow-500" />
                        ) : (
                          <StarOff className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </CardHeader>
                  
                  <CardContent className="pt-0">
                    <div className="text-sm text-gray-600 mb-4">
                      <div className="flex items-center gap-4">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(document.created_at)}
                        </span>
                        <span className="text-gray-400">•</span>
                        <span>{document.file_size}</span>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center justify-between">
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                          onClick={() => viewDocument(document)}
                        >
                          <Eye className="w-3 h-3 mr-1" />
                          View
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-gray-300 text-gray-700 hover:bg-gray-50"
                          onClick={() => downloadDocument(document)}
                        >
                          <Download className="w-3 h-3 mr-1" />
                          Download
                        </Button>
                      </div>
                      
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 p-2"
                          onClick={() => {
                            // TODO: Implement edit functionality
                            toast({
                              title: "Edit Feature",
                              description: "Document editing coming soon",
                            });
                          }}
                        >
                          <Edit3 className="w-3 h-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 p-2"
                          onClick={() => deleteDocument(document.id)}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Document Preview Modal */}
      <DocumentPreviewModal
        document={selectedDocument}
        isOpen={previewModalOpen}
        onClose={() => {
          setPreviewModalOpen(false);
          setSelectedDocument(null);
        }}
        onDownload={downloadDocument}
      />
    </div>
  );
};

export default DocumentVault;